import * as React from 'react';
import { ReactNode, ReactElement } from 'react';
import {
    Edit as RaEdit,
    EditBase,
    EditProps as RaEditProps,
    EditView as RaEditView,
    useCheckMinimumRequiredProps,
    usePermissions,
    useRecordContext,
    useResourceContext,
    useTranslate,
} from 'react-admin';
import { Button, Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import History from '@mui/icons-material/History';
import { canAccess } from '../canAccess';

import { EditActions } from './EditActions';

/**
 * Replacement for react-admin's Edit that adds RBAC control to actions
 *
 * Users must have the 'edit' permission on the resource and record to see the Edit view.
 * Users must have the 'show' permission on the resource and record to see the ShowButton.
 * Users must have the 'clone' permission on the resource and record to see the CloneButton.
 *
 * @example
 * import { Edit } from '@react-admin/ra-rbac';
 *
 * const authProvider = {
 *      // ...
 *      getPermissions: () => Promise.resolve([
 *          { action: ['list', 'edit', 'clone'], resource: 'products' },
 *      ]),
 * };
 *
 * export const PostEdit = () => (
 *     <Edit>
 *         ...
 *     </Edit>
 * );
 * // user will see the clone button but not the show button
 */
export const Edit = (props: EditProps) => {
    useCheckMinimumRequiredProps('Edit', ['children'], props);
    const {
        resource,
        id,
        mutationMode,
        mutationOptions,
        queryOptions,
        redirect,
        transform,
        disableAuthentication,
        ...rest
    } = props;
    return (
        <EditBase
            resource={resource}
            id={id}
            mutationMode={mutationMode}
            mutationOptions={mutationOptions}
            queryOptions={queryOptions}
            redirect={redirect}
            transform={transform}
            disableAuthentication={disableAuthentication}
        >
            <EditView actions={<EditActions />} {...rest} />
        </EditBase>
    );
};

interface EditProps extends RaEditProps {
    children: ReactNode;
    unauthorized?: ReactElement;
}

Edit.propTypes = RaEdit.propTypes;

const EditView = (
    props: Parameters<typeof RaEditView>[0] & {
        unauthorized?: ReactElement;
    }
) => {
    const resource = useResourceContext();
    const record = useRecordContext();
    const { isLoading, permissions } = usePermissions();
    if (isLoading) {
        return null;
    }
    if (
        !canAccess({
            permissions,
            action: 'edit',
            resource,
            record,
        })
    ) {
        return props.unauthorized ?? <DefaultUnauthorizedView />;
    }
    return <RaEditView {...props} />;
};

const DefaultUnauthorizedView = () => {
    const translate = useTranslate();
    return (
        <Box
            sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                [theme.breakpoints.down('md')]: {
                    padding: '1em',
                },
                fontFamily: 'Roboto, sans-serif',
                opacity: 0.5,
                '& h1': {
                    display: 'flex',
                    alignItems: 'center',
                },
            })}
        >
            <h1 role="alert">
                <LockIcon
                    sx={{
                        width: '2em',
                        height: '2em',
                        marginRight: '0.5em',
                    }}
                />
                {`Unauthorized`}
            </h1>
            <div>{`You have no permission to access this page.`}</div>
            <Box
                sx={{
                    marginTop: '2em',
                }}
            >
                <Button
                    variant="contained"
                    startIcon={<History />}
                    onClick={goBack}
                >
                    {translate('ra.action.back')}
                </Button>
            </Box>
        </Box>
    );
};

function goBack() {
    window.history.go(-1);
}
